import type { Stamp } from '../core/paint/stroke';
import { loadImageUrl } from './imageIO';

/**
 * Turning stamps into pixels.
 *
 * A stroke is rendered into a **mask** at full alpha first, then the mask is
 * composited onto the layer once with the stroke's opacity. That is what makes
 * opacity behave: a slow stroke with a thousand overlapping dabs lands the same
 * colour as a fast one with ten. Compositing each dab at `opacity` instead
 * would make speed change the colour — the classic "why is my brush blotchy"
 * bug.
 */

export interface StampRenderer {
  /** CSS colour, or null when erasing. */
  color: string | null;
  /** Diameter in the target's own pixels. */
  size: number;
  /** 0..1 */
  hardness: number;
}

function drawDab(ctx: CanvasRenderingContext2D, stamp: Stamp, renderer: StampRenderer): void {
  const radius = Math.max(0.5, renderer.size / 2);
  const hardness = Math.max(0, Math.min(1, renderer.hardness));
  const alpha = Math.max(0, Math.min(1, stamp.alpha));
  if (alpha === 0) return;

  const color = renderer.color ?? '#000000';
  ctx.save();
  ctx.globalAlpha = alpha;

  if (hardness >= 0.999) {
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.arc(stamp.x, stamp.y, radius, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
    return;
  }

  const gradient = ctx.createRadialGradient(
    stamp.x,
    stamp.y,
    radius * hardness,
    stamp.x,
    stamp.y,
    radius,
  );
  gradient.addColorStop(0, color);
  gradient.addColorStop(1, withAlpha(color, 0));
  ctx.fillStyle = gradient;
  ctx.beginPath();
  ctx.arc(stamp.x, stamp.y, radius, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

/** Append stamps to a mask context; call again for each new batch of the stroke. */
export function drawStamps(
  ctx: CanvasRenderingContext2D,
  stamps: readonly Stamp[],
  renderer: StampRenderer,
): void {
  for (const stamp of stamps) drawDab(ctx, stamp, renderer);
}

/** `#rrggbb` / `#rrggbbaa` → `rgba(...)`, so a gradient stop can fade to nothing. */
export function withAlpha(color: string, alpha: number): string {
  const hex = color.trim();
  const match = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(hex);
  if (!match) return color;
  const digits = match[1] as string;
  const full =
    digits.length === 3
      ? digits
          .split('')
          .map((digit) => digit + digit)
          .join('')
      : digits;
  const value = Number.parseInt(full, 16);
  const r = (value >> 16) & 255;
  const g = (value >> 8) & 255;
  const b = value & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export interface BakeOptions {
  /** Current layer pixels, or null for a blank layer. */
  src: string | null;
  width: number;
  height: number;
  stamps: readonly Stamp[];
  renderer: StampRenderer;
  /** 0..1 stroke opacity, applied once to the whole mask. */
  opacity: number;
  erase?: boolean;
}

/**
 * Paint a finished stroke into a layer and return the layer's new PNG.
 *
 * The layer is never mutated: the caller commits the result as one history
 * entry, so undo restores the pre-stroke pixels exactly.
 */
export async function bakeStroke(options: BakeOptions): Promise<string> {
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(options.width));
  canvas.height = Math.max(1, Math.round(options.height));
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('This browser cannot render a canvas.');

  if (options.src) {
    const image = await loadImageUrl(options.src);
    ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
  }

  const mask = document.createElement('canvas');
  mask.width = canvas.width;
  mask.height = canvas.height;
  const maskCtx = mask.getContext('2d');
  if (!maskCtx) throw new Error('This browser cannot render a canvas.');
  drawStamps(maskCtx, options.stamps, options.renderer);

  ctx.globalAlpha = Math.max(0, Math.min(1, options.opacity));
  ctx.globalCompositeOperation = options.erase ? 'destination-out' : 'source-over';
  ctx.drawImage(mask, 0, 0);

  return canvas.toDataURL('image/png');
}

/**
 * A live preview that composites the same mask the bake will use, so what the
 * user watches is what gets committed rather than an approximation of it.
 */
export class StrokePreview {
  private readonly mask: HTMLCanvasElement;
  private readonly maskCtx: CanvasRenderingContext2D;
  private readonly renderer: StampRenderer;
  private readonly opacity: number;
  private readonly erase: boolean;
  private snapshot: HTMLCanvasElement | null = null;
  private drawn = 0;

  readonly canvas: HTMLCanvasElement;

  constructor(
    width: number,
    height: number,
    renderer: StampRenderer,
    opacity: number,
    erase: boolean,
  ) {
    this.renderer = renderer;
    this.opacity = opacity;
    this.erase = erase;

    this.canvas = document.createElement('canvas');
    this.canvas.width = Math.max(1, Math.round(width));
    this.canvas.height = Math.max(1, Math.round(height));
    this.mask = document.createElement('canvas');
    this.mask.width = this.canvas.width;
    this.mask.height = this.canvas.height;
    const maskCtx = this.mask.getContext('2d');
    if (!maskCtx) throw new Error('This browser cannot render a canvas.');
    this.maskCtx = maskCtx;
  }

  /** The layer's current pixels, drawn beneath the mask during the stroke. */
  setBase(src: string | null): void {
    if (!src) {
      this.snapshot = null;
      return;
    }
    void loadImageUrl(src).then((image) => {
      const base = document.createElement('canvas');
      base.width = this.canvas.width;
      base.height = this.canvas.height;
      base.getContext('2d')?.drawImage(image, 0, 0, base.width, base.height);
      this.snapshot = base;
      this.render();
    });
  }

  /** Append the new stamps and repaint. */
  append(stamps: readonly Stamp[]): void {
    if (stamps.length === 0) return;
    drawStamps(this.maskCtx, stamps, this.renderer);
    this.drawn += stamps.length;
    this.render();
  }

  get stampCount(): number {
    return this.drawn;
  }

  private render(): void {
    const ctx = this.canvas.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    ctx.globalCompositeOperation = 'source-over';
    ctx.globalAlpha = 1;
    if (this.snapshot) ctx.drawImage(this.snapshot, 0, 0);

    ctx.globalAlpha = Math.max(0, Math.min(1, this.opacity));
    ctx.globalCompositeOperation = this.erase ? 'destination-out' : 'source-over';
    ctx.drawImage(this.mask, 0, 0);
    ctx.globalAlpha = 1;
    ctx.globalCompositeOperation = 'source-over';
  }

  destroy(): void {
    this.mask.width = 0;
    this.mask.height = 0;
    this.canvas.width = 0;
    this.canvas.height = 0;
    this.snapshot = null;
  }
}

/** What the stage needs from any live stroke preview. */
export interface StrokePreviewSurface {
  readonly canvas: HTMLCanvasElement;
  append(stamps: readonly Stamp[]): void;
  destroy(): void;
}

/** How a hidden area is shown while a mask is being painted. */
export const MASK_VEIL = '#000000';
export const MASK_VEIL_ALPHA = 0.6;

/**
 * A preview for painting a **layer mask**, which is the opposite problem.
 *
 * Painting a mask adds no colour — it changes what is hidden — and an overlay
 * canvas sitting on top of the editor cannot un-paint the pixels underneath it.
 * So instead of drawing the result, this draws *what the stroke hides*: a dark
 * veil across the layer, punched through wherever the mask lets the layer show,
 * and punched further by every dab of the brush. Erasing lets the veil back in.
 *
 * The user watches coverage change rather than a white blob that will vanish on
 * commit, and the veil is exactly the shape the mask will have.
 */
export class MaskVeilPreview implements StrokePreviewSurface {
  readonly canvas: HTMLCanvasElement;

  private readonly stamps: HTMLCanvasElement;
  private readonly stampCtx: CanvasRenderingContext2D;
  private readonly renderer: StampRenderer;
  private readonly opacity: number;
  private readonly erase: boolean;
  private stencil: HTMLCanvasElement | null = null;
  private drawn = 0;

  constructor(
    width: number,
    height: number,
    renderer: StampRenderer,
    opacity: number,
    erase: boolean,
  ) {
    this.renderer = renderer;
    this.opacity = opacity;
    this.erase = erase;

    this.canvas = document.createElement('canvas');
    this.canvas.width = Math.max(1, Math.round(width));
    this.canvas.height = Math.max(1, Math.round(height));
    this.stamps = document.createElement('canvas');
    this.stamps.width = this.canvas.width;
    this.stamps.height = this.canvas.height;
    const stampCtx = this.stamps.getContext('2d');
    if (!stampCtx) throw new Error('This browser cannot render a canvas.');
    this.stampCtx = stampCtx;
  }

  /** The mask as it stands before this stroke; the veil starts from it. */
  setStencil(src: string | null): void {
    if (!src) {
      this.stencil = null;
      return;
    }
    void loadImageUrl(src).then((image) => {
      const stencil = document.createElement('canvas');
      stencil.width = this.canvas.width;
      stencil.height = this.canvas.height;
      stencil.getContext('2d')?.drawImage(image, 0, 0, stencil.width, stencil.height);
      this.stencil = stencil;
      this.render();
    });
  }

  append(stamps: readonly Stamp[]): void {
    if (stamps.length === 0) return;
    drawStamps(this.stampCtx, stamps, this.renderer);
    this.drawn += stamps.length;
    this.render();
  }

  get stampCount(): number {
    return this.drawn;
  }

  private render(): void {
    const ctx = this.canvas.getContext('2d');
    if (!ctx) return;
    const { width, height } = this.canvas;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, width, height);

    // Start from "everything is hidden", then take away what the mask shows.
    ctx.globalCompositeOperation = 'source-over';
    ctx.globalAlpha = MASK_VEIL_ALPHA;
    ctx.fillStyle = MASK_VEIL;
    ctx.fillRect(0, 0, width, height);
    if (this.stencil) {
      ctx.globalCompositeOperation = 'destination-out';
      ctx.globalAlpha = 1;
      ctx.drawImage(this.stencil, 0, 0);
    }

    // Painting punches the veil; erasing puts it back.
    ctx.globalAlpha = Math.max(0, Math.min(1, this.opacity));
    ctx.globalCompositeOperation = this.erase ? 'source-over' : 'destination-out';
    ctx.drawImage(this.stamps, 0, 0);

    ctx.globalAlpha = 1;
    ctx.globalCompositeOperation = 'source-over';
  }

  destroy(): void {
    this.canvas.width = 0;
    this.canvas.height = 0;
    this.stamps.width = 0;
    this.stamps.height = 0;
    if (this.stencil) {
      this.stencil.width = 0;
      this.stencil.height = 0;
    }
    this.stencil = null;
  }
}
